import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { hexagramBits } from '../core/iching-core';
import { relationsOf } from '../core/iching-relations';
import { ICHING_TRIGRAMS } from '../core/iching-trigrams.const';
import { IChingIndexEntry, IChingRelationKind } from '../core/iching.models';
import { HdIChingHexagramFigureComponent } from '../figure/hd-iching-hexagram-figure.component';

interface RelationCardVm {
  readonly kind: IChingRelationKind;
  readonly number: number;
  readonly title: string;
  readonly figure: string;
  /** Valt samen met het hexagram zelf (bijvoorbeeld bij 1 en 2). */
  readonly same: boolean;
}

/**
 * Verwante hexagrammen van één hexagram: tegenhanger (錯 cuo), omgekeerd
 * (綜 zong), gewisseld (交 jiao), kern (互 hu) en het paleishoofd (八宮, Jing
 * Fang), elk als kaartje met mini-figuur, nummer en titel; daaronder de acht
 * paleisleden als chips met het huidige hexagram gemarkeerd. Titels komen uit
 * de index van de actieve bron (`entries`); zonder index blijft de titel leeg.
 * Klik emit het nummer; de host navigeert. Klassieke termen, geen
 * HexWave-benamingen.
 */
@Component({
  selector: 'hd-iching-relations',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdIChingHexagramFigureComponent],
  styles: `
    :host { display: block; container-type: inline-size; }
    .rel__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
    @container (max-width: 420px) { .rel__grid { grid-template-columns: minmax(0, 1fr); } }
    .rel__card {
      display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 4px 10px; align-items: center; text-align: left;
      padding: 8px 10px; border: 1px solid var(--border-color, rgba(0, 0, 0, 0.12)); border-radius: 8px;
      background: transparent; color: inherit; font: inherit; cursor: pointer;
    }
    .rel__card:hover:not(:disabled) { border-color: var(--primary); }
    .rel__card:disabled { opacity: 0.6; cursor: default; }
    .rel__card--palace { grid-column: 1 / -1; cursor: default; }
    .rel__kind { grid-column: 1 / -1; font-size: 0.78rem; color: var(--text-secondary); }
    .rel__label { display: inline-flex; flex-wrap: wrap; align-items: baseline; gap: 0 6px; min-width: 0; }
    .rel__number { font-weight: 600; }
    .rel__title { font-size: 0.9rem; }
    .rel__chips { display: flex; flex-wrap: wrap; gap: 4px; grid-column: 1 / -1; }
    .rel__chip {
      padding: 2px 8px; border-radius: 999px; border: 1px solid var(--border-color, rgba(0, 0, 0, 0.12));
      background: transparent; color: inherit; font: inherit; font-size: 0.8rem; cursor: pointer;
    }
    .rel__chip:hover:not(:disabled) { border-color: var(--primary); }
    .rel__chip.is-current { background: var(--primary); color: var(--on-primary, #fff); border-color: var(--primary); cursor: default; }
  `,
  template: `
    <section class="rel iching-block">
      <h3 class="iching-block__heading">{{ t('humanDesign.iching.relations.title') }}</h3>
      <div class="rel__grid">
        @for (card of cards(); track card.kind) {
          <button
            type="button"
            class="rel__card"
            data-test-id="iching-relation"
            [attr.data-number]="card.number"
            [attr.data-kind]="card.kind"
            [class.is-same]="card.same"
            [disabled]="card.same"
            [title]="t('humanDesign.iching.relations.' + card.kind + 'Hint')"
            (click)="hexagramSelected.emit(card.number)"
          >
            <span class="rel__kind">
              {{ t('humanDesign.iching.relations.' + card.kind) }}@if (card.same) { · {{ t('humanDesign.iching.relations.same') }} }
            </span>
            <hd-iching-hexagram-figure size="mini" [figure]="card.figure" />
            <!-- Gap i.p.v. een spatie: Angular strips witruimte tussen elementen. -->
            <span class="rel__label"><span class="rel__number">{{ card.number }}</span><span class="rel__title">{{ card.title }}</span></span>
          </button>
        }
        <div class="rel__card rel__card--palace" [title]="t('humanDesign.iching.relations.palaceHint')">
          <span class="rel__kind">
            {{ t('humanDesign.iching.relations.palace') }} · {{ trigrams[palace().trigram].symbol }} {{ trigrams[palace().trigram].pinyin }}
          </span>
          <div class="rel__chips">
            @for (member of palace().members; track member) {
              <button
                type="button"
                class="rel__chip"
                data-test-id="iching-palace-member"
                [class.is-current]="member === number()"
                [disabled]="member === number()"
                [attr.aria-label]="t('humanDesign.iching.relations.open', { number: member })"
                (click)="hexagramSelected.emit(member)"
              >{{ member }}</button>
            }
          </div>
        </div>
      </div>
    </section>
  `,
})
export class HdIChingRelationsComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly number = input.required<number>();
  readonly entries = input<readonly IChingIndexEntry[] | undefined>(undefined);
  readonly hexagramSelected = output<number>();

  protected readonly trigrams = ICHING_TRIGRAMS;
  private readonly relations = computed(() => relationsOf(this.number()));
  protected readonly palace = computed(() => this.relations().palace);

  /** Vier relatiekaartjes; het paleis staat apart met zijn chips. */
  protected readonly cards = computed<RelationCardVm[]>(() => {
    const r = this.relations();
    const byNumber = new Map((this.entries() ?? []).map(e => [e.number, e] as const));
    const card = (kind: IChingRelationKind, n: number): RelationCardVm => ({
      kind,
      number: n,
      title: byNumber.get(n)?.title ?? '',
      figure: hexagramBits(n),
      same: n === r.number,
    });
    return [card('opposite', r.opposite), card('inverted', r.inverted), card('exchanged', r.exchanged), card('nuclear', r.nuclear)];
  });

  protected t(key: string, params?: Record<string, unknown>): string {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(key, params, this.contentLang.effectiveLang());
  }
}
